import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons'
import { faLinkedin, faSquareFacebook, faSquareInstagram, faSquareXTwitter, faSquareYoutube } from '@fortawesome/free-brands-svg-icons'
import ApiUrl from '../../configTools/api'

// Ported from the source project's src/components/site/Footer.tsx, plus the content the
// old public/topbar.jsx and public/bottombar.jsx carried (neither has a counterpart in the
// ported design, so their content is folded in here rather than dropped):
//   - topbar's "We provide digital solutions" tagline -> prepended to the brand paragraph.
//   - bottombar's "Privacy Policy" link -> moved into the Contact column, now a real
//     react-router Link to /privacypolicy instead of a same-page anchor.
//   - bottombar's "Infection Control" text was NOT linked to anything in the old markup
//     (plain text, no <a>/<Link>, and nothing in the app defines an Infection Control
//     route or section) - it pointed nowhere, so it is dropped rather than carried forward.
//
// The old footer's newsletter/subscriber form (src/layout/public/footer/web.jsx +
// functions.js + subscribermessage/) was the ONLY write path to POST /api/public/subscriber
// (src/pages/admin/subscribers/ still reads that table), and the social-icon row pointed at
// the lab's real profiles - both are load-bearing product features, not presentation, so both
// are rebuilt here rather than left dropped. Neither has a counterpart in the ported design,
// so the request/response contract and the social URLs are recovered verbatim from git
// (commit 4257d1c: footer/functions.js, footer/subscribermessage/, footer/web.jsx) and the
// markup/CSS around them is new, built from this file's existing design tokens - see the
// "FOOTER NEWSLETTER & SOCIAL" banner in site.css.
const SOCIAL_LINKS = [
    { icon: faSquareFacebook, label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=100093019306708' },
    { icon: faSquareXTwitter, label: 'X (Twitter)', href: 'https://twitter.com/Nvisiondental' },
    { icon: faLinkedin, label: 'LinkedIn', href: 'https://pk.linkedin.com/in/n-vision-dental-3b206b279' },
    { icon: faSquareInstagram, label: 'Instagram', href: 'https://www.instagram.com/nvisiondental/' },
    { icon: faSquareYoutube, label: 'YouTube', href: 'https://www.youtube.com/channel/UCqYMbk_6ktRqoDcNR1xxYtw' }
];

const Footer = ({ settings, services }) => {
    const year = new Date().getFullYear();

    // Same request/response contract as the old footer/functions.js: POST {email} as JSON to
    // ApiUrl.Public_Subscriber, then show data.response/data.success exactly as
    // footer/subscribermessage/ did.
    const [email, setEmail] = useState('');
    const [emailPostRes, setEmailPostRes] = useState(null);
    const [showEmailPostRes, setShowEmailPostRes] = useState(false);

    const submitSubscribe = () => {
        if (email === '') {
            alert('Enter email !!');
            return;
        }
        fetch(ApiUrl.Public_Subscriber, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        })
            .then((res) => res.json())
            .then((data) => {
                setEmailPostRes(data);
                setShowEmailPostRes(true);
                setEmail('');
            });
    };

    return (
        <footer className="site-footer">
            <div className="footer-inner">
                <div className="footer-top">
                    <div className="footer-brand">
                        <div className="nav-logo">
                            <div className="logo-mark">{settings.logoMark}</div>
                            <div className="logo-text">
                                {settings.brandName}
                                <span>{settings.brandSub}</span>
                            </div>
                        </div>
                        <p>
                            We provide digital solutions. Precision digital dental restorations for practices across
                            Pakistan and the USA. CAD/CAM technology, fast turnaround, and a portal that keeps you in
                            control.
                        </p>
                        <div className="footer-social">
                            {SOCIAL_LINKS.map((soc) => (
                                <a
                                    key={soc.href}
                                    className="footer-social-icon"
                                    href={soc.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={soc.label}
                                >
                                    <FontAwesomeIcon icon={soc.icon} />
                                </a>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="footer-col-title">Services</div>
                        <ul className="footer-links">
                            {services.slice(0, 5).map((s) => (
                                <li key={s.order}>
                                    <Link to="/services">{s.name}</Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="footer-col-title">Company</div>
                        <ul className="footer-links">
                            <li><Link to="/about">About us</Link></li>
                            {/* No standalone page for these two yet - placeholder anchors, same as the source design. */}
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                            <li><a href="#">Our technology</a></li>
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                            <li><a href="#">Fee schedule</a></li>
                            <li><Link to="/login">Client portal</Link></li>
                        </ul>
                    </div>
                    <div>
                        <div className="footer-col-title">Contact</div>
                        <ul className="footer-links">
                            <li><a href={`mailto:${settings.contact.email}`}>{settings.contact.email}</a></li>
                            <li><a href={`tel:${settings.contact.phone}`}>{settings.contact.phone}</a></li>
                            <li><Link to="/privacypolicy">Privacy policy</Link></li>
                            {/* No standalone terms page yet - placeholder anchor, same as the source design. */}
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                            <li><a href="#">Terms of service</a></li>
                        </ul>
                    </div>
                </div>

                <div className="footer-newsletter">
                    <div className="footer-newsletter-text">
                        <div className="footer-col-title">Newsletter</div>
                        <p className="footer-newsletter-desc">Subscribe to receive updates, access to exclusive deals, and more.</p>
                    </div>
                    <div className="footer-newsletter-form">
                        <input
                            className="footer-newsletter-input"
                            type="text"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyPress={(e) => { if (e.key === 'Enter') submitSubscribe(); }}
                        />
                        <button type="button" className="btn-primary" onClick={submitSubscribe}>Subscribe</button>
                    </div>
                </div>

                <div className="footer-bottom">
                    <span className="footer-copy">© {year} {settings.brandName} {settings.brandSub}. All rights reserved.</span>
                    <span className="footer-copy">{settings.contact.addressLine}</span>
                </div>
            </div>

            {showEmailPostRes &&
                <div className="footer-subscribe-modal" onClick={() => setShowEmailPostRes(false)}>
                    <div className="footer-subscribe-panel" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="footer-subscribe-close"
                            aria-label="Close"
                            onClick={() => setShowEmailPostRes(false)}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                        <div className={`footer-subscribe-icon ${emailPostRes.success ? 'is-success' : 'is-error'}`}>
                            <FontAwesomeIcon icon={emailPostRes.success ? faCheck : faXmark} />
                        </div>
                        <p className="footer-subscribe-message">{emailPostRes.response}</p>
                    </div>
                </div>
            }
        </footer>
    );
};

export default Footer;
